import { useModuleStore } from '@/store/moduleStore'
import { getActiveModuleMenuItems } from '../../modules'
import { i18n } from '@/plugins/i18n'

/**
 * Converts a module manifest menu item into the shape consumed by the sidebar.
 *
 * Module menu entries are registered declaratively inside `modules/manifest.ts`
 * and stitched into the main navigation at runtime.
 */
function convertModuleMenuItem(item: any, allItems: any[]): any {
  const children = allItems.filter((entry) => entry.parent === item.key)

  return {
    label: i18n.global.t(item.label),
    icon: item.icon?.replace('pi pi-', 'fa-solid fa-') || 'fa-solid fa-box',
    to: item.path,
    allowedRoles: item.roles || ['all'],
    items:
      children.length > 0
        ? children.map((child) => convertModuleMenuItem(child, allItems))
        : undefined
  }
}

/**
 * Returns the complete admin-center navigation model.
 *
 * The menu is composed of:
 * 1. core static entries owned by the application shell
 * 2. dynamically injected module entries based on tenant module state
 *
 * The tenant argument acts as a lightweight readiness check so the shell can
 * avoid rendering incomplete navigation while the session is still loading.
 */
export function getMenuItems(tenant: any) {
  const moduleStore = useModuleStore()
  const itemsEmpty = [{}]

  const items = [
    {
      label: 'Home',
      icon: 'fa-solid fa-house',
      to: '/',
      allowedRoles: ['all']
    },
    {
      label: 'Reports',
      icon: 'fa-solid fa-chart-line',
      to: '/reports/general',
      allowedRoles: ['all']
    },
    {
      label: 'Fragen',
      icon: 'fa fa-question',
      to: '/questionCategories',
      allowedRoles: ['ai_lab_admin', 'ai_lab_user']
    },
    {
      label: 'Ergebnisseite',
      icon: 'fa-solid fa-chart-bar',
      to: '/evaluation/default',
      allowedRoles: ['ai_lab_admin', 'ai_lab_user']
    },
    /*
    {
      label: 'Custom FAQ',
      icon: 'fa-person-circle-question',
      to: '/faqs_categories',
      allowedRoles: ['ai_lab_admin', 'ai_lab_user', 'mandant_admin']
    },
    */
    {
      label: 'Translations',
      icon: 'fa-solid fa-language',
      to: '/tenant/translations',
      allowedRoles: ['all']
    },
    {
      label: 'Layout',
      icon: 'fa-solid fa-paintbrush',
      to: '/tenant/layout',
      allowedRoles: ['ai_lab_admin', 'mandant_admin']
    },
    {
      label: 'Flags',
      icon: 'fa-solid fa-flag',
      to: '/flags',
      allowedRoles: ['ai_lab_admin', 'ai_lab_user']
    },
    {
      label: 'Handbuch',
      icon: 'fa-solid fa-book',
      to: '#',
      href: (import.meta as any).env.VITE_USER_DOCS_URL || 'http://localhost:5180',
      external: true,
      allowedRoles: ['all'],
      bottom: true
    },
    {
      label: 'Instance',
      icon: 'fa-solid fa-clone',
      to: '/tenant/info',
      allowedRoles: ['ai_lab_admin', 'mandant_admin']
    },
    {
      label: 'Settings',
      icon: 'fa-solid fa-cog',
      to: '/tenant/settings',
      allowedRoles: ['ai_lab_admin', 'mandant_admin'],
      items: [
        { label: 'General', icon: 'fa-solid fa-cogs', to: '/tenant/settings/general' },
        { label: 'Modules', icon: 'fa-solid fa-box', to: '/tenant/settings/modules' },
        { label: 'Translations', icon: 'fa-solid fa-language', to: '/tenant/settings/translations' }
      ]
    },
    {
      label: 'Admin Settings',
      icon: 'fa-solid fa-toolbox',
      to: '/admin/settings',
      allowedRoles: ['ai_lab_admin'],
      items: [
        { label: 'Users', icon: 'fa-solid fa-user', to: '/admin/settings/users' },
        { label: 'Tenants', icon: 'fa-solid fa-users', to: '/admin/settings/tenants' },
        { label: 'Module', icon: 'fa-solid fa-box', to: '/admin/settings/modules' },
        { label: 'Translations', icon: 'fa-solid fa-language', to: '/admin/settings/translations' },
        { label: 'Exports', icon: 'fa-solid fa-download', to: '/admin/settings/exports' },
        { label: 'Migrationen', icon: 'fa-solid fa-database', to: '/admin/settings/migrations' },
        { label: 'Mail Settings', icon: 'fa-solid fa-envelope', to: '/admin/settings/mail' },
        {
          label: 'Global Message',
          icon: 'fa-solid fa-message',
          to: '/admin/settings/globalMessage'
        },
        {
          label: 'Customization Helpers',
          icon: 'fa-solid fa-paintbrush',
          to: '/admin/settings/customization'
        },
        { label: 'Themes', icon: 'fa-solid fa-palette', to: '/admin/settings/themes' },
        { label: 'Testing', icon: 'fa-solid fa-flask', to: '/admin/settings/testing' }
      ]
    }
  ]

  const activeModules = moduleStore.enabledModules
  const userRoles = ['ai_lab_admin', 'mandant_admin'] // TODO: resolve this from the live session
  const moduleMenuItems = getActiveModuleMenuItems(activeModules, userRoles)
  const topLevelItems = moduleMenuItems.filter((item) => !item.parent)

  // Insert module sections before tenant settings so feature modules appear as
  // part of the main workspace rather than buried in global administration.
  const settingsIndex = items.findIndex((item) => item.to === '/tenant/settings')

  for (const moduleItem of topLevelItems) {
    const convertedItem = convertModuleMenuItem(moduleItem, moduleMenuItems)
    if (settingsIndex > -1) {
      items.splice(settingsIndex, 0, convertedItem)
    } else {
      items.push(convertedItem)
    }
  }

  return tenant ? items : itemsEmpty
}
